/**
 * FaucetButton: "Get Test FRAME" for the Base Sepolia demo.
 * - Visible only when a wallet is connected AND that wallet is on Base Sepolia (84532).
 * - Reads cooldown from GET /check/:addr before offering a claim; POST /request only on click.
 * - The header instance also owns the global `nt3d:request-faucet` event (onboarding "Step 1").
 * variant="header": holo button for the top bar (compact icon on phones).
 * variant="menu":   full-width row, for a wallet dropdown (see PR #25).
 */
import { useAccount } from 'wagmi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ethers } from 'ethers';
import { CHAIN } from '../config/chain';

export const FAUCET_URL = (import.meta.env.VITE_FAUCET_URL as string | undefined) || 'https://nt3d-faucet.wurkit.workers.dev';
export const FAUCET_CHAIN_ID = 84532;
const EXPLORER = 'https://sepolia.basescan.org';

type FaucetResponse = { success?: boolean; pending?: boolean; reverted?: boolean; amount?: string; txHash?: string; error?: string };

/** Read the wallet's test FRAME balance straight from the chain (null if unknown). */
async function readFrameBalance(account: string): Promise<bigint | null> {
  if (!CHAIN.frameTokenAddress || !CHAIN.rpcUrl) return null;
  try {
    const provider = new ethers.JsonRpcProvider(CHAIN.rpcUrl, FAUCET_CHAIN_ID, { staticNetwork: true });
    const token = new ethers.Contract(CHAIN.frameTokenAddress, ['function balanceOf(address) view returns (uint256)'], provider);
    const timeout = new Promise<null>(res => setTimeout(() => res(null), 8000));
    return await Promise.race([token.balanceOf(account) as Promise<bigint>, timeout]);
  } catch {
    return null;
  }
}

/**
 * The faucet can report an error (or the request can time out) AFTER the transfer was already
 * broadcast and mined, e.g. when the worker's wait for the receipt or its bookkeeping throws.
 * Before telling the user it failed, check whether their test FRAME balance actually went up.
 */
async function claimLandedOnChain(account: string, before: bigint | null, attempts = 4, delayMs = 3000): Promise<boolean> {
  if (before === null) return false;
  for (let i = 0; i < attempts; i++) {
    const now = await readFrameBalance(account);
    if (now !== null && now > before) return true;
    if (i < attempts - 1) await new Promise(r => setTimeout(r, delayMs));
  }
  return false;
}

/** Errors the worker returns before it broadcasts anything; no need to check the chain for these. */
const PRE_SEND_ERROR = /invalid address|cannot use the faucet|please wait|faucet is empty/i;
const mayHaveSent = (d: FaucetResponse) => !d.reverted && (!!d.txHash || !d.error || !PRE_SEND_ERROR.test(d.error));

type CheckState = { status: 'idle' | 'checking' | 'ready' | 'cooldown' | 'error'; hoursLeft?: number };

export function useFaucet(account: string | null) {
  const { chainId: walletChain } = useAccount();
  const chainId = account ? (walletChain ?? null) : null;
  const onSepolia = chainId === FAUCET_CHAIN_ID;
  const [check, setCheck] = useState<CheckState>({ status: 'idle' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!account) { setCheck({ status: 'idle' }); return; }
    setCheck(c => ({ ...c, status: c.status === 'idle' ? 'checking' : c.status }));
    try {
      const r = await fetch(`${FAUCET_URL}/check/${account.toLowerCase()}`, { signal: AbortSignal.timeout(10000) });
      const d = await r.json() as { canRequest?: boolean; hoursLeft?: number };
      setCheck(d.canRequest ? { status: 'ready' } : { status: 'cooldown', hoursLeft: d.hoursLeft });
    } catch {
      setCheck({ status: 'error' });
    }
  }, [account]);

  useEffect(() => { if (account && onSepolia) void refresh(); }, [account, onSepolia, refresh]);

  const request = useCallback(async () => {
    if (!account) { toast.error('Connect your wallet first.'); return; }
    if (!onSepolia) { toast.error(`Switch your wallet to ${CHAIN.chainName || 'Base Sepolia'} to use the faucet.`); return; }
    if (check.status === 'cooldown') { toast(`Faucet cooldown: try again in about ${check.hoursLeft ?? 24}h.`, { icon: '⏳' }); return; }
    if (busy) return;
    setBusy(true);
    const tid = toast.loading('Requesting test FRAME… (the faucet waits for the transfer to confirm)');
    const before = await readFrameBalance(account);
    const succeeded = (d: FaucetResponse) => {
      const amt = (d.amount || '').replace(/\s*FRAME$/i, '') || 'your';
      toast.success(
        <span>{d.pending ? 'Sending' : 'Sent'} {amt} test FRAME (testnet only, no value).{d.txHash && <> <a href={`${EXPLORER}/tx/${d.txHash}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>View tx</a></>}</span>,
        { id: tid, duration: 7000 },
      );
      setCheck({ status: 'cooldown', hoursLeft: 24 });
      window.dispatchEvent(new CustomEvent('nt3d:faucet-success', { detail: { txHash: d.txHash } }));
    };
    try {
      // The worker waits for the receipt, so allow a generous timeout.
      const r = await fetch(`${FAUCET_URL}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ address: account.toLowerCase() }),
        signal: AbortSignal.timeout(60000),
      });
      const d = await r.json().catch(() => ({})) as FaucetResponse;
      if (r.ok && d.success) {
        succeeded(d);
      } else if (mayHaveSent(d) && await claimLandedOnChain(account, before)) {
        // Worker said it failed, but the transfer reached the wallet: report the truth.
        succeeded({ ...d, success: true, pending: false });
      } else {
        toast.error(d.error || `Faucet error (HTTP ${r.status}). Try again later.`, { id: tid, duration: 6000 });
        void refresh();
      }
    } catch (e) {
      if (await claimLandedOnChain(account, before)) {
        succeeded({ success: true });
      } else {
        const msg = e instanceof Error && e.name === 'TimeoutError' ? 'The faucet is taking too long. Check your balance in a minute before retrying.' : 'Could not reach the faucet. Check your connection and try again.';
        toast.error(msg, { id: tid, duration: 6000 });
        void refresh();
      }
    } finally {
      setBusy(false);
    }
  }, [account, onSepolia, check, busy, refresh]);

  return { chainId, onSepolia, check, busy, request, refresh };
}

interface Props { account: string | null; variant?: 'header' | 'menu'; /** listen for the onboarding nt3d:request-faucet event (mount exactly one) */ ownsEvent?: boolean }

export default function FaucetButton({ account, variant = 'header', ownsEvent = false }: Props) {
  const f = useFaucet(account);
  const reqRef = useRef(f.request);
  reqRef.current = f.request;

  useEffect(() => {
    if (!ownsEvent) return;
    const h = () => { void reqRef.current(); };
    window.addEventListener('nt3d:request-faucet', h);
    return () => window.removeEventListener('nt3d:request-faucet', h);
  }, [ownsEvent]);

  if (!account || !f.onSepolia) return null;

  const cooling = f.check.status === 'cooldown';
  const label = f.busy ? 'Sending…' : cooling ? `Faucet in ${f.check.hoursLeft ?? '?'}h` : 'Get Test FRAME';
  const title = cooling
    ? `You already claimed. Next claim in about ${f.check.hoursLeft}h (24h cooldown per wallet).`
    : f.check.status === 'error' ? 'Faucet status unknown. Click to try anyway.' : 'Claim free test FRAME (testnet only, no value). One claim per wallet every 24h.';

  if (variant === 'menu') {
    return (
      <button type="button" className="holo-btn ghost sm faucet-btn faucet-menu" style={{ width: '100%' }} onClick={() => void f.request()} disabled={f.busy || cooling} title={title} data-testid="faucet-menu">
        <span aria-hidden="true">🚰</span> {label}
      </button>
    );
  }
  return (
    <button type="button" className={`holo-btn ${cooling ? 'ghost' : ''} sm faucet-btn`} onClick={() => void f.request()} disabled={f.busy || cooling} title={title} aria-label={label} data-testid="faucet-header">
      <span aria-hidden="true">🚰</span><span className="faucet-lbl">{label}</span>
    </button>
  );
}
